import React, { useRef, useState, useEffect } from 'react';
import { Bold, Italic, Underline, Palette, Type, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Image } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder, minHeight = '100px', className = '', showBottomToolbar = true, onInsertImage }) => {
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [showBottomColorPicker, setShowBottomColorPicker] = useState(false);
  const selectionRef = useRef(null);
  const lastValueRef = useRef(value);

  // Sync editor content with value prop - including clearing when value is empty
  useEffect(() => {
    if (editorRef.current) {
      // If value is empty/cleared, clear the editor
      if (!value && lastValueRef.current) {
        editorRef.current.innerHTML = '';
      }
      // If value changed externally and editor is empty, set it
      else if (value && !editorRef.current.innerHTML) {
        editorRef.current.innerHTML = value;
      }
      lastValueRef.current = value;
    }
  }, [value]);

  // Save selection
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      selectionRef.current = sel.getRangeAt(0).cloneRange();
      return true;
    }
    return false;
  };

  // Restore selection
  const restoreSelection = () => {
    if (selectionRef.current && editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(selectionRef.current);
      return true;
    }
    return false;
  };

  // Check selection and show toolbar
  const checkSelection = () => {
    const sel = window.getSelection();
    const selectedText = sel?.toString()?.trim();
    
    // Show toolbar if any text is selected (even just 1 character)
    if (selectedText && selectedText.length > 0 && editorRef.current?.contains(sel.anchorNode)) {
      try {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        
        // Calculate position - handle both short and long selections
        const toolbarWidth = 280;
        let left = rect.left - editorRect.left + rect.width / 2 - toolbarWidth / 2;
        
        // Ensure toolbar stays within editor bounds
        left = Math.max(0, Math.min(left, editorRect.width - toolbarWidth));
        
        // Position above selection, or below if not enough space
        let top = rect.top - editorRect.top - 50;
        if (top < 0) {
          top = rect.bottom - editorRect.top + 10;
        }
        
        setToolbarPosition({ top, left });
        saveSelection();
        setShowToolbar(true);
      } catch (e) {
        console.log('Selection error:', e);
      }
    } else {
      if (!showColorPicker && !showSizePicker) {
        setShowToolbar(false);
      }
    }
  };

  // Apply format command
  const execFormat = (command, commandValue = null) => {
    restoreSelection();
    
    try {
      document.execCommand(command, false, commandValue);
      
      if (editorRef.current && onChange) {
        onChange(editorRef.current.innerHTML);
      }
      
      // Re-save selection
      setTimeout(() => {
        saveSelection();
      }, 10);
    } catch (e) {
      console.error('execCommand error:', e);
    }
    
    setShowColorPicker(false);
    setShowSizePicker(false);
  };

  // Handle input
  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Insert image at cursor position
  const insertImageAtCursor = (imageUrl) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    
    // Create image element
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.borderRadius = '8px';
    img.style.margin = '8px 0';
    img.style.display = 'block';
    
    // Insert at cursor
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(img);
      // Move cursor after image
      range.setStartAfter(img);
      range.setEndAfter(img);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      editorRef.current.appendChild(img);
    }
    
    if (onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Handle image file selection
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }
    
    // If onInsertImage callback provided, use it (for server upload)
    if (onInsertImage) {
      try {
        const imageUrl = await onInsertImage(file);
        if (imageUrl) {
          insertImageAtCursor(imageUrl);
        }
      } catch (err) {
        console.error('Image upload failed:', err);
        alert('Failed to upload image');
      }
    } else {
      // Fallback: Convert to base64 and insert directly
      const reader = new FileReader();
      reader.onload = (event) => {
        insertImageAtCursor(event.target.result);
      };
      reader.readAsDataURL(file);
    }
    
    // Reset input
    e.target.value = '';
  };

  // Colors
  const colors = [
    { name: 'Black', value: '#000000' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Blue', value: '#2563eb' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Purple', value: '#9333ea' },
    { name: 'Orange', value: '#ea580c' },
    { name: 'Pink', value: '#db2777' },
    { name: 'Gray', value: '#6b7280' },
  ];

  // Sizes
  const sizes = [
    { name: 'Small', value: '1' },
    { name: 'Normal', value: '3' },
    { name: 'Large', value: '5' },
    { name: 'X-Large', value: '6' },
    { name: 'XX-Large', value: '7' },
  ];

  return (
    <div className={`relative ${className}`}>
      {/* Floating Toolbar */}
      {showToolbar && (
        <div 
          className="absolute z-[500] flex items-center gap-0.5 p-1.5 bg-white border border-gray-200 rounded-lg shadow-xl"
          style={{ top: `${toolbarPosition.top}px`, left: `${toolbarPosition.left}px` }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Bold */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); }}
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-4 h-4" />
          </button>
          
          {/* Italic */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); }}
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-4 h-4" />
          </button>
          
          {/* Underline */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onMouseDown={(e) => { e.preventDefault(); execFormat('underline'); }}
            title="Underline (Ctrl+U)"
          >
            <Underline className="w-4 h-4" />
          </button>
          
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          
          {/* Color Picker */}
          <div className="relative">
            <button
              type="button"
              className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors ${showColorPicker ? 'bg-gray-100' : ''}`}
              onMouseDown={(e) => { 
                e.preventDefault(); 
                saveSelection();
                setShowColorPicker(!showColorPicker); 
                setShowSizePicker(false); 
              }}
              title="Text Color"
            >
              <Palette className="w-4 h-4" />
            </button>
            {showColorPicker && (
              <div 
                className="absolute top-full left-0 mt-1 p-2 bg-white border rounded-lg shadow-xl z-[600] min-w-[140px]"
                onMouseDown={(e) => e.preventDefault()}
              >
                <p className="text-xs font-medium text-gray-500 mb-2">Text Color</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {colors.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-gray-400 hover:scale-110 transition-all"
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                      onMouseDown={(e) => { e.preventDefault(); execFormat('foreColor', c.value); }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Size Picker */}
          <div className="relative">
            <button
              type="button"
              className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors ${showSizePicker ? 'bg-gray-100' : ''}`}
              onMouseDown={(e) => { 
                e.preventDefault(); 
                saveSelection();
                setShowSizePicker(!showSizePicker); 
                setShowColorPicker(false); 
              }}
              title="Text Size"
            >
              <Type className="w-4 h-4" />
            </button>
            {showSizePicker && (
              <div 
                className="absolute top-full left-0 mt-1 py-1 bg-white border rounded-lg shadow-xl z-[600] min-w-[100px]"
                onMouseDown={(e) => e.preventDefault()}
              >
                <p className="text-xs font-medium text-gray-500 mb-1 px-3">Text Size</p>
                {sizes.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 transition-colors"
                    onMouseDown={(e) => { e.preventDefault(); execFormat('fontSize', s.value); }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          
          {/* Bullet List */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onMouseDown={(e) => { e.preventDefault(); execFormat('insertUnorderedList'); }}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          
          {/* Numbered List */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onMouseDown={(e) => { e.preventDefault(); execFormat('insertOrderedList'); }}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 focus:outline-none overflow-auto bg-white"
        style={{ minHeight }}
        onInput={handleInput}
        onMouseUp={() => setTimeout(checkSelection, 50)}
        onKeyUp={(e) => {
          if (e.shiftKey) setTimeout(checkSelection, 50);
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!showColorPicker && !showSizePicker) {
              setShowToolbar(false);
            }
          }, 200);
        }}
        onPaste={(e) => {
          e.preventDefault();
          
          // Get pasted content
          const clipboardData = e.clipboardData || window.clipboardData;
          
          // Try to get HTML content first (preserves formatting)
          let pastedContent = clipboardData.getData('text/html');
          
          if (pastedContent) {
            // Clean up the HTML but preserve structure
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = pastedContent;
            
            // Remove any script tags for security
            const scripts = tempDiv.querySelectorAll('script');
            scripts.forEach(s => s.remove());
            
            // Insert the cleaned HTML
            document.execCommand('insertHTML', false, tempDiv.innerHTML);
          } else {
            // Fallback to plain text - convert line breaks to <br> tags
            let text = clipboardData.getData('text/plain');
            
            // Preserve line breaks by converting to <br>
            text = text.replace(/\r\n/g, '\n'); // Normalize Windows line breaks
            text = text.replace(/\n/g, '<br>'); // Convert newlines to <br>
            
            // Preserve multiple spaces (convert to &nbsp;)
            text = text.replace(/  /g, ' &nbsp;');
            
            // Insert the formatted text
            document.execCommand('insertHTML', false, text);
          }
          
          // Trigger onChange
          if (editorRef.current && onChange) {
            onChange(editorRef.current.innerHTML);
          }
        }}
        onKeyDown={(e) => {
          if (e.ctrlKey || e.metaKey) {
            if (e.key === 'b') { e.preventDefault(); execFormat('bold'); }
            if (e.key === 'i') { e.preventDefault(); execFormat('italic'); }
            if (e.key === 'u') { e.preventDefault(); execFormat('underline'); }
          }
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
      
      {/* Close pickers overlay */}
      {(showColorPicker || showSizePicker || showBottomColorPicker) && (
        <div 
          className="fixed inset-0 z-[400]" 
          onMouseDown={() => { setShowColorPicker(false); setShowSizePicker(false); setShowBottomColorPicker(false); }}
        />
      )}

      {/* Persistent Bottom Toolbar */}
      {showBottomToolbar && (
        <div className="flex items-center gap-1 p-2 border-t bg-gray-50 rounded-b-lg">
          {/* Bold */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('bold'); }}
            title="Bold (Ctrl+B)"
          >
            <Bold className="w-4 h-4" />
          </button>
          
          {/* Italic */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('italic'); }}
            title="Italic (Ctrl+I)"
          >
            <Italic className="w-4 h-4" />
          </button>
          
          {/* Underline */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('underline'); }}
            title="Underline (Ctrl+U)"
          >
            <Underline className="w-4 h-4" />
          </button>
          
          <div className="w-px h-5 bg-gray-300 mx-1" />
          
          {/* Bullet List */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('insertUnorderedList'); }}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          
          {/* Numbered List */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('insertOrderedList'); }}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          
          <div className="w-px h-5 bg-gray-300 mx-1" />
          
          {/* Color Picker for Bottom Toolbar */}
          <div className="relative">
            <button
              type="button"
              className={`w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600 ${showBottomColorPicker ? 'bg-gray-200' : ''}`}
              onMouseDown={(e) => { 
                e.preventDefault(); 
                editorRef.current?.focus();
                setShowBottomColorPicker(!showBottomColorPicker); 
              }}
              title="Text Color"
            >
              <Palette className="w-4 h-4" />
            </button>
            {showBottomColorPicker && (
              <div 
                className="absolute bottom-full left-0 mb-1 p-2 bg-white border rounded-lg shadow-xl z-[600] min-w-[140px]"
                onMouseDown={(e) => e.preventDefault()}
              >
                <p className="text-xs font-medium text-gray-500 mb-2">Text Color</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {colors.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-gray-400 hover:scale-110 transition-all"
                      style={{ backgroundColor: c.value }}
                      title={c.name}
                      onMouseDown={(e) => { e.preventDefault(); execFormat('foreColor', c.value); setShowBottomColorPicker(false); }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Alignment buttons */}
          <div className="w-px h-5 bg-gray-300 mx-1" />
          
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('justifyLeft'); }}
            title="Align Left"
          >
            <AlignLeft className="w-4 h-4" />
          </button>
          
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('justifyCenter'); }}
            title="Align Center"
          >
            <AlignCenter className="w-4 h-4" />
          </button>
          
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { e.preventDefault(); editorRef.current?.focus(); execFormat('justifyRight'); }}
            title="Align Right"
          >
            <AlignRight className="w-4 h-4" />
          </button>
          
          <div className="w-px h-5 bg-gray-300 mx-1" />
          
          {/* Insert Image */}
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-200 transition-colors text-gray-600"
            onMouseDown={(e) => { 
              e.preventDefault(); 
              imageInputRef.current?.click();
            }}
            title="Insert Image"
          >
            <Image className="w-4 h-4" />
          </button>
          
          {/* Hidden file input for images */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageSelect}
          />
        </div>
      )}
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] ul, [contenteditable] ol {
          padding-left: 1.5rem;
          margin: 0.25rem 0;
        }
        [contenteditable] li {
          margin: 0.15rem 0;
        }
      `}</style>
    </div>
  );
};

export { RichTextEditor };
